<template>
  <v-container class="pa-0">
    <v-row
      no-gutters
      wrap
      mx-2>
      <v-col
        cols="12"
        xs="12"
        class="pa-1">
        <h1
          :class="!isEmbedded ? 'headline black--text text-center font-weight-medium mb-1' : 'black--text text-center font-weight-medium headline mb-0 px-4'"
          v-html="$t('booked.booked',
                     { date: `${meetingStartDate.format('ddd, MMM D')} ${formatHour(meetingStartDate, selectedMeetingType.timezone)}-${formatHour(meetingEndDate, selectedMeetingType.timezone, true)}` })"

        />
        <div
          :class="!isEmbedded ? 'body-1 pb-3 px-3 text-center grey--text text--darken-3 my-4' : 'body-2 text-center grey--text darken-3 mt-2'"
          v-html="$t('booked.confirmation', { email: createdMeeting.meeting.email })"/>

      </v-col>
    </v-row>
    <v-row
      :class="isEmbedded ? '' : 'Booked__info border--radius-box'"
      no-gutters
      wrap>
      <v-col

        cols="12">
        <!-- Meeting Header -->
        <v-row
          no-gutters
        >
          <v-col
            :class="isEmbedded ? 'blue lighten-5 mb-3 mt-2 pb-2' : ''"
            cols="12"
          >
            <meeting-header-info
              :location="createdMeeting.meeting.location"
              :duration="duration"
              :host-picture="createdMeeting.host.pictureUrl"
              :host-name="createdMeeting.host.fullName"
              :is-embedded="isEmbedded"/>
          </v-col>
        </v-row>
        <v-divider v-if="!isEmbedded"/>
        <v-row
          :class="isMobile ? 'px-1 py-6 mb-9' : 'pa-7 mb-9'"
          no-gutters
          justify="center">
          <v-col
            cols="12"
            xs="12"
            justify="center"
            class="text-center"
          >
            <v-card
              flat
              class="d-flex justify-center">
              <v-img
                :width="isEmbedded ? '64px' : '100px'"
                :height="isEmbedded ? '64px' : '100px'"
                contain
                src="/assets/calendar.png"
                class="justify-center"/></v-card>
          </v-col>
          <v-col
            :class="isEmbedded ? 'mt-3 subtitle-1 black--text font-weight-bold text-center pb-0' : 'mt-9 title black--text font-weight-bold text-center'"
            cols="12">
            {{ selectedMeetingType.meetingTitle }}
            <span class="font-weight-regular"> with </span>
            {{ createdMeeting.host.fullName }}
          </v-col>
          <v-col
            v-if="meetingStartDate"
            :class="isEmbedded ? 'subtitle-1 text-center black--text pt-0' : 'title text-center black--text'"
            cols="12">
            {{ meetingStartDate.format('D') }}<sup>{{ getNth(meetingStartDate.format('D')) }}</sup>
            {{ meetingStartDate.format(' MMMM YYYY, ') }}{{ formatHour(meetingStartDate, selectedMeetingType.timezone) }} - {{ formatHour(meetingEndDate, selectedMeetingType.timezone, true) }}
            {{ selectedMeetingType.timezone }}
          </v-col>
          <!-- Reschedule | Cancel meeting -->
          <v-row
            :class="!isEmbedded ? 'pa-0' : 'pa-1'"
            no-gutters
            wrap>

            <v-col
              :class="!isEmbedded ? 'body-1 text-md-right pa-3 text-right caption' : 'body-1 text-md-left pa-3 text-right'"
              cols="6"
              xs="6">
              <a
                class="subtitle-1 font-weight-bold"
                @click="goTo('reschedule')">{{ $t('booked.reschedule') }}</a>
            </v-col>
            <v-col
              :class="!isEmbedded ? 'body-1 text-left pa-3 caption' : 'body-1 text-left pa-3 '"
              cols="6"
              xs="6">
              <a
                class="subtitle-1 red--text font-weight-regular"
                @click="goTo('cancel')"> {{ $t('booked.cancel') }}</a>
            </v-col>
          </v-row>
          <!-- Thank you -->
          <v-col
            cols="12"
            class="body-2 text-center mt-6 black--text font-weight-bold">{{ $t('booked.thanks') }}
          </v-col>
          <v-col
            v-if="!whiteLabel"
            cols="12"
            class="body-2 text-center black--text mb-4">{{ $t('booked.createYouOwn') }}
          </v-col>
          <!-- Get Gmelius -->
          <v-col
            cols="12"
            class="body-1 text-center">
            <v-btn
              v-if="!whiteLabel"
              color="primary"
              rounded
              outlined
              ripple
              x-large
              class="capitalize--text font-weight-bold icon-google pl-12"
              @click.native="installGmelius">
              <span class="ml-2">
                {{ $t('booked.installGmelius') }}
              </span>
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
      <v-snackbar
        v-model="snackbar"
        :timeout="10000"
        left>
        {{ $t('snackBar.booked') }}
      </v-snackbar>
    </v-row>
  </v-container>
</template>

<script>
import { localeMixin, isMobileMixin } from '../mixins'
import { nth } from '../modules/utils'
import { externalLinks } from '../config'
import { mapGetters } from 'vuex'
import moment from 'moment'
import md5 from 'md5'
import MeetingHeaderInfo from './MeetingHeaderInfo.vue'

export default {
  components: {
    MeetingHeaderInfo
  },
  mixins: [localeMixin, isMobileMixin],
  props: {
    isEmbedded: {
      type: String,
      default: null
    },
    whiteLabel: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      duration: 0,
      meetingStartDate: null,
      meetingEndDate: null,
      snackbar: true
    }
  },
  computed: {
    ...mapGetters('meetings', [
      'selectedMeetingType',
      'createdMeeting'
    ])
  },
  created () {
    this.meetingStartDate = moment.unix(this.createdMeeting.meeting.startTimestamp).tz(this.selectedMeetingType.timezone)
    this.meetingEndDate = moment.unix(this.createdMeeting.meeting.endTimestamp).tz(this.selectedMeetingType.timezone)
    this.duration = (this.createdMeeting.meeting.endTimestamp - this.createdMeeting.meeting.startTimestamp) / 60
  },
  methods: {
    goTo (pageName) {
      if (this.createdMeeting) {
        const meeting = this.createdMeeting.meeting
        const secret = this.createdMeeting.secret
        const query = {
          m: meeting.meetingId,
          i: md5(meeting.email),
          s: meeting.startTimestamp,
          e: meeting.endTimestamp,
          sec: secret,
          booked: true
        }
        if (this.isEmbedded) {
          query.embed = 1
        }
        this.$router.push({
          name: pageName,
          params: {
            prefixMeetingUrl: this.$route.params.prefixMeetingUrl,
            meetingTypeUrl: this.$route.params.meetingTypeUrl
          },
          query: query
        })
        this.$emit(pageName)
      }
    },
    installGmelius () {
      window.open(externalLinks.dashboardUrl, '_blank')
    },
    getNth (date) {
      return nth(date)
    }
  }
}
</script>
<style lang="scss" scoped>
.Booked__info {
  border: 1px solid #dedede;
}
</style>
